<template>
  <q-select
    :model-value="selectedTags"
    mode="tags"
    multiline
    label="name"
    :theme="theme"
    :placeholder="placeholder || $t('conversations.filters.tag_picker.placeholder')"
    :no-results-text="$t('dashboard.sidebar.helpdesk_tags_empty')"
    :close-on-select="false"
    :selectable="(option) => option.id"
    :options="tagOptions"
    :class="brandTheme && $style.brandTheme"
    :filter-results="false"
    :open-direction="openDirection"
    @update:model-value="tagsSelected"
    @search-change="getTags"
  >
    <template #afterlist>
      <span v-if="moreCount > 0">
        {{ $t('universal.n_more', { n: $n(moreCount) }, moreCount) }}
      </span>
    </template>
    <template #tag="{ option }">
      <slot name="tag" :option="option">
        {{ option.name }}
      </slot>
    </template>
  </q-select>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref } from 'vue'
import { debounce, uniqBy } from 'lodash-es'

import QSelect from '@/components/QSelect.vue'

export interface Option {
  value?: string
  label: string
}

export interface ApiResponse {
  tags: Option[]
  total: number
}

export default defineComponent({
  name: 'TagPicker',
  components: {
    QSelect,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: { type: Array as PropType<Option[] | string[]> },
    apiMethod: { type: Function as PropType<(query: string) => Promise<ApiResponse>>, required: true },
    brandTheme: Boolean,
    placeholder: String,
    theme: String,
    object: {
      type: Boolean,
      default: true,
    },
    openDirection: {
      type: String as PropType<'top' | 'bottom' | 'auto'>,
      default: 'bottom',
    },
  },
  setup(props, { emit }) {
    const tagList = ref<Option[]>([])
    const query = ref('')
    const moreCount = ref(0)

    const getTags = debounce(
      async function (search) {
        query.value = search
        const { tags, total } = await props.apiMethod(search)
        const availableTags = tags.filter((t) => {
          if (props.object) {
            return !(props.modelValue as Option[]).includes(t)
          } else {
            return !(props.modelValue as string[]).includes(t.value ?? '')
          }
        })
        moreCount.value = total - availableTags?.length
        const existingPickedTags = tagList.value.filter((t) => {
          if (props.object) {
            return (props.modelValue as Option[]).includes(t)
          } else {
            return (props.modelValue as string[]).includes(t.value ?? '')
          }
        })
        tagList.value = uniqBy(existingPickedTags.concat(tags), 'value').sort()
      },
      300,
      { leading: true },
    )

    onMounted(() => {
      const initialTags = props.object
        ? ([...props.modelValue] as Option[])
        : props.modelValue.map((tag) => ({ value: tag, label: tag }))
      tagList.value = initialTags
      getTags('')
    })

    return {
      query,
      tagList,
      selectedTags: computed(() => (props.object ? props.modelValue.map((v) => v.value) : props.modelValue)),
      tagOptions: computed(() => tagList.value.map((t) => ({ id: t.value, name: t.label }))),
      tagsSelected(tags: string[]) {
        emit('update:modelValue', props.object ? tags.map((t) => ({ label: t, value: t })) : tags)
      },
      getTags,
      moreCount,
    }
  },
})
</script>

<style lang="scss" module>
.more {
  color: var(--gray-60);
}

:global(.multiselect-tags) {
  max-height: 9rem;
  overflow-y: auto;
  width: 16rem;
}

.brandTheme :global(.multiselect-tags) {
  overflow: hidden;

  :global(.multiselect-tag) {
    background-color: theme('colors.active.primary');
    color: var(--white);
    font-weight: theme('fontWeight.normal');
    overflow: hidden;

    span:first-child {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}
</style>
